import React, { useState } from 'react';
import {
    Box, Typography, TextField, InputAdornment, Card, Tabs, Tab, Stack,
    List, ListItem, ListItemText, ListItemAvatar, Avatar, Divider,
    Tooltip, IconButton, Menu, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const AttendanceTabs = ({ counts, filteredGroups, searchTerm, setSearchTerm, tabValue, setTabValue, isTrainer, onTrainerRsvp }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedUserForStatus, setSelectedUserForStatus] = useState(null);

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUserForStatus(null);
    };

    const handleRsvp = (variant) => {
        if (selectedUserForStatus) {
            onTrainerRsvp(selectedUserForStatus, variant);
        }
        handleMenuClose();
    };

    const StatusList = ({ users, type }) => (
        <List sx={{ width: '100%', bgcolor: 'background.paper', py: 0 }}>
            {users.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center', opacity: 0.6 }}>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>Keine passenden Spieler gefunden.</Typography>
                </Box>
            )}
            {users.map((u, index) => (
                <React.Fragment key={`${type}-${u.id}`}>
                    <ListItem
                        sx={{ px: 2, py: 1, '&:hover': { bgcolor: 'action.hover' } }}
                        secondaryAction={
                            isTrainer ? (
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Zusage">
                                        <span>
                                            <IconButton size="small" color="success" onClick={() => onTrainerRsvp(u.id, 'zusage')} disabled={type === 'zusage'}>
                                                <CheckIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Absage">
                                        <span>
                                            <IconButton size="small" color="error" onClick={() => onTrainerRsvp(u.id, 'absage')} disabled={type === 'absage'}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <IconButton size="small" onClick={(e) => {
                                        setAnchorEl(e.currentTarget);
                                        setSelectedUserForStatus(u.id);
                                    }}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            ) : null
                        }
                    >
                        <ListItemAvatar>
                            <Avatar
                                src={u.profilbild_url}
                                sx={{
                                    width: 40, height: 40,
                                    bgcolor: type === 'zusage' ? 'success.light' : type === 'absage' ? 'error.light' : 'grey.300',
                                    fontSize: '0.9rem'
                                }}
                            >
                                {u.first_name?.[0]}{u.last_name?.[0]}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={
                                <Typography variant="body1" fontWeight="500">
                                    {u.first_name} {u.last_name}
                                </Typography>
                            }
                            secondary={
                                u.notiz
                                    ? <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>„{u.notiz}"</Typography>
                                    : (u.alias || u.role)
                            }
                        />
                    </ListItem>
                    {index < users.length - 1 && <Divider component="li" variant="inset" />}
                </React.Fragment>
            ))}
        </List>
    );

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight="bold">Anwesenheit</Typography>
                <TextField
                    size="small"
                    placeholder="Spieler suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            <Card sx={{ borderRadius: "12px", overflow: 'hidden' }}>
                <Tabs
                    value={tabValue}
                    onChange={(e, v) => setTabValue(v)}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<Stack direction="row" spacing={1} alignItems="center"><CheckCircleIcon /> <Typography variant="body2">({counts?.zusagen || 0})</Typography></Stack>} />
                    <Tab icon={<Stack direction="row" spacing={1} alignItems="center"><CancelIcon /> <Typography variant="body2">({counts?.absagen || 0})</Typography></Stack>} />
                    <Tab icon={<Stack direction="row" spacing={1} alignItems="center"><HelpIcon /> <Typography variant="body2">({counts?.vielleicht || 0})</Typography></Stack>} />
                    <Tab icon={<Stack direction="row" spacing={1} alignItems="center"><PersonOutlineIcon /> <Typography variant="body2">({counts?.keine_rueckmeldung || 0})</Typography></Stack>} />
                </Tabs>
                <Box>
                    {filteredGroups && (
                        <>
                            {tabValue === 0 && <StatusList users={filteredGroups.zusagen} type="zusage" />}
                            {tabValue === 1 && <StatusList users={filteredGroups.absagen} type="absage" />}
                            {tabValue === 2 && <StatusList users={filteredGroups.vielleicht} type="vielleicht" />}
                            {tabValue === 3 && <StatusList users={filteredGroups.keine_rueckmeldung} type="none" />}
                        </>
                    )}
                </Box>
            </Card>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleRsvp('zusage')}>Zusage</MenuItem>
                <MenuItem onClick={() => handleRsvp('absage')}>Absage</MenuItem>
                <MenuItem onClick={() => handleRsvp('vielleicht')}>Vielleicht</MenuItem>
            </Menu>
        </Box>
    );
};

export default AttendanceTabs;
